import { type GitCiStatus } from '@apm/contracts';
import { useQuery } from '@tanstack/react-query';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { displayDateTime } from '../../../lib/date';
import { gitApi, gitKeys } from '../api';
import { type GitRepositoryCardSlotProps } from '../types';
import { GitCiDot, GitExternalLink } from './git-marks';

/** Thứ một dòng CI cần — chung cho `defaultBranchCi` (bây giờ) và lượt CI đọc tại `asOf`. */
interface CiPoint {
  readonly status: GitCiStatus;
  readonly webUrl: string | null;
  readonly createdAtExt: string;
  readonly finishedAt: string | null;
}

/**
 * CI của **nhánh mặc định** trên thẻ kho (màn Tổng quan) — APM-GIT-i,
 * docs/plan-git-integration.md §8.1 "CI nhánh chính". Cắm ở
 * `GIT_REPOSITORY_CARD_SLOTS`.
 *
 * ## `asOf` (luật #7)
 *
 * Đọc "bây giờ" thì `GitRepositoryView.defaultBranchCi` đã mang sẵn lượt mới nhất
 * — **không** lời gọi nào thêm. Đang xem quá khứ thì hỏi `GET …/pipelines`
 * (`limit=1`, `asOf`) cho đúng kho: lượt mới nhất **tạo trước mốc**, trạng thái tại
 * mốc ấy (lượt xong sau mốc thì lúc ấy còn "đang chạy"). Không dùng
 * `defaultBranchCi` cho quá khứ: chấm xanh của hôm nay đứng cạnh một mốc tháng
 * trước trông y hệt một màn đúng.
 *
 * Kho chưa có lượt CI nào trong bản sao (kho GitHub không dùng Actions, CI ngoài
 * chỉ báo qua commit status) ⇒ một câu nói thẳng thế, không một chấm xám.
 */
export function GitCiCardSlot({
  projectId,
  repository,
  asOf,
}: GitRepositoryCardSlotProps): ReactNode {
  const past = useQuery({
    queryKey: gitKeys.pipelines(projectId, repository.id, 1, asOf),
    queryFn: () => gitApi.pipelines(projectId, repository.id, 1, asOf),
    enabled: asOf !== undefined,
  });

  let point: CiPoint | null | undefined;
  if (asOf === undefined) point = repository.defaultBranchCi ?? null;
  else if (past.data) point = past.data.items[0] ?? null;
  // Đang tải / lỗi khi xem quá khứ: không vẽ gì thay vì vẽ trạng thái của hôm nay.
  if (point === undefined) return null;

  return <CiLine branch={repository.defaultBranch} point={point} />;
}

/** ⚠️ Không nhận asOf (luật #7, ngoại lệ 1): thuần trình bày — mốc là việc của `GitCiCardSlot`. */
function CiLine({
  branch,
  point,
}: {
  readonly branch: string | null;
  readonly point: CiPoint | null;
}): ReactNode {
  const { t } = useTranslation();
  if (point === null) {
    return (
      <p data-testid="git-ci-card" data-status="none" className="text-xs text-muted-foreground">
        {t('gitTab.ci.none')}
      </p>
    );
  }
  return (
    <p
      data-testid="git-ci-card"
      data-status={point.status}
      className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs"
    >
      <span className="text-muted-foreground">
        {t('gitTab.ci.line', { branch: branch ?? '—' })}
      </span>
      <GitCiDot status={point.status} url={point.webUrl} />
      <GitExternalLink url={point.webUrl} className="font-medium">
        {t(`gitTab.ci.status.${point.status}`)}
      </GitExternalLink>
      <span className="text-muted-foreground">
        · {displayDateTime(point.finishedAt ?? point.createdAtExt)}
      </span>
    </p>
  );
}
